import { createAppSlice } from "../../app/createAppSlice"
import { createEntityAdapter, isAnyOf } from '@reduxjs/toolkit'
import { startAppListening } from "../../app/listenerMiddleware"
import { changedResourceAdded } from "../changedResourcesList/changedResourcesListSlice"
import getIdFromReference from "../../functions/getIdFromReference"
import addFoisFromFlis from "../../functions/addFoisFromFlisAPI"
import { codeMatchesPercentage, codeMatchesProportion } from "../../resourceComponents/compositionResourceFunctions/summaryOfFindingsFunctions"
import { selectCurrentUserApiToken } from "../currentUser/currentUserSlice"

const loadBaselineRiskCollection = async (findingsSection,
    apiToken, combinedDictionary = {}, resourceCollection) => {
    if (resourceCollection.length > 0 && Object.keys(combinedDictionary).length === 0) {
        for (const entry of resourceCollection) {
            combinedDictionary[entry.id] = entry.revisedResourceJson;
        }
    }
    let baselineRiskCollection = [];
    let permissionDictionary = {};
    for (const entry of resourceCollection) {
        permissionDictionary[entry.id] = entry.usageData.editpermission;
    }
    let baselineRiskResources = [];
    let fliListToProcess = [];
    baselineRiskResources = findingsSection.map(section => {
        let entry;
        let targetFoi = getIdFromReference(section.focus);
        let targetTitle = section.focus.display || combinedDictionary[targetFoi].title || combinedDictionary[targetFoi].name;
        if (!isNaN(targetFoi) && Array.isArray(section.section)) {
            for (let subsection of section.section) {
                if (subsection.code?.coding?.length > 0 &&
                    (subsection.code.coding[0].code === "evidence-with-comparator-alone" ||
                        subsection.code.coding[0].code === "result-with-comparator-alone" ||
                        subsection.code.coding[0].code === "Control-group-alone-Evidence")) {
                    if (subsection.entry?.length) {
                        let sourcefoi = subsection.entry[0].reference?.replace("Evidence/", "") || "missing";
                        if (!isNaN(sourcefoi)) {
                            entry = {
                                id: targetFoi,
                                targetTitle: targetTitle,
                                baselineRiskFoi: sourcefoi
                            };
                        } else if (subsection.entry[0].identifier?.system === "https://fevir.net/FLI" && subsection.entry[0].identifier.value) {
                            let sourcefli = subsection.entry[0].identifier.value;
                            entry = {
                                id: targetFoi,
                                targetTitle: targetTitle,
                                baselineRiskFoi: NaN,
                                fli: sourcefli
                            };
                            fliListToProcess.push(sourcefli);
                        }
                    }
                }
            }
        }
        return entry;
    });
    if (fliListToProcess.length > 0) {
        let response = await addFoisFromFlis(apiToken, baselineRiskResources);
        if (response?.success && response.foilist?.length && response.flitofoi) {
            for (let resource of baselineRiskResources) {
                if ((!resource?.baselineRiskFoi || isNaN(resource.baselineRiskFoi)) &&
                    resource?.fli && response.flitofoi[resource.fli]) {
                    resource.baselineRiskFoi = response.flitofoi[resource.fli];
                }
            }
        }
    }
    if (baselineRiskResources.length > 0) {
        baselineRiskCollection = baselineRiskResources.map(entry => {
            let baselineRiskFoi = entry.baselineRiskFoi;
            let baselineRiskResource = combinedDictionary[baselineRiskFoi];
            let controlEventRate = NaN;
            let controlEventRateDisplay = "[Not a proportion.]";
            for (const stat of baselineRiskResource.statistic) {
                if (stat.statisticType && stat.statisticType.coding && stat.statisticType.coding.length > 0) {
                    if (codeMatchesProportion(stat.statisticType.coding[0].code)) {
                        if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                            controlEventRate = stat.quantity.value * 100;
                            controlEventRateDisplay = parseFloat(controlEventRate.toFixed(2)) + '%';
                            break;
                        }
                    }
                    if (codeMatchesPercentage(stat.statisticType.coding[0].code)) {
                        if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                            controlEventRate = stat.quantity.value;
                            controlEventRateDisplay = parseFloat(controlEventRate.toFixed(2)) + '%';
                            break;
                        }
                    }
                    if (stat.statisticType.coding[0].code === 'STATO:0000047') {
                        if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value) && stat.sampleSize) {
                            let denominator = stat.sampleSize.numberAnalyzed || stat.sampleSize.knownDataCount || stat.sampleSize.numberOfParticipants || 0;
                            if (denominator > 0) {
                                controlEventRate = (stat.quantity.value / denominator) * 100;
                                controlEventRateDisplay = parseFloat(controlEventRate.toFixed(2)) + '%';
                                break;
                            }
                        }
                    }
                }
            }
            return {
                id: entry.id,
                targetTitle: entry.targetTitle,
                baselineRiskFoi: baselineRiskFoi,
                fli: entry.fli,
                editPermission: permissionDictionary[baselineRiskFoi],
                controlEventRate: controlEventRate,
                controlEventRateDisplay: controlEventRateDisplay,
                resource: baselineRiskResource,
                reference: {
                    reference: "Evidence/" + baselineRiskFoi,
                    type: "Evidence",
                    display: baselineRiskResource.title || baselineRiskResource.name
                }
            }
        });
    }
    return baselineRiskCollection;
};

//entity: {  id: entry.id,  ---the targetFoi for the EvidenceVariable---
//            targetTitle: entry.targetTitle,
//            baselineRiskFoi: baselineRiskFoi,
//            fli: entry.fli,
//            editPermission: permissionDictionary[baselineRiskFoi],
//            controlEventRate: controlEventRate,
//            controlEventRateDisplay: controlEventRateDisplay,
//            resource: baselineRiskResource,
//            reference: {
//               reference: "Evidence/" + baselineRiskFoi,
//               type: "Evidence",
//               display: baselineRiskResource.title || baselineRiskResource.name
//            }
// }
// 
//key uses: customized Summary of Findings and Summary of Net Effect, Change Baseline Risks

const baselineRiskAdapter = createEntityAdapter({
    sortComparer: (b, a) => {
        if (b?.targetTitle && a?.targetTitle && b.targetTitle !== a.targetTitle) {
            return b.targetTitle.localeCompare(a.targetTitle)
        } else if (b?.id && a?.id && b.id !== a.id) {
            return b.id.localeCompare(a.id)
        } else {
            return 0
        }
    }
})

const initialState = baselineRiskAdapter.getInitialState({
    status: 'idle',
    error: null
})

const baselineRiskSlice = createAppSlice({
    name: "baselineRisk",
    initialState,
    reducers: create => ({
        baselineRiskAdded: create.preparedReducer(
            (baselineRiskBody) => {
                return {
                    payload: {
                        id: baselineRiskBody.id,
                        targetTitle: baselineRiskBody.targetTitle,
                        baselineRiskFoi: baselineRiskBody.baselineRiskFoi,
                        fli: baselineRiskBody.fli,
                        editPermission: baselineRiskBody.editPermission,
                        controlEventRate: baselineRiskBody.controlEventRate,
                        controlEventRateDisplay: baselineRiskBody.controlEventRateDisplay,
                        resource: baselineRiskBody.resource,
                        reference: baselineRiskBody.reference
                    }
                };
            },
            baselineRiskAdapter.addOne
        ),
        baselineRiskUpdated: create.reducer((state, action) => {
            const { id, targetTitle, baselineRiskFoi, fli, editPermission,
                controlEventRate, controlEventRateDisplay, resource, reference } = action.payload;
            baselineRiskAdapter.updateOne(state,
                {
                    id, changes: {
                        targetTitle, baselineRiskFoi, fli, editPermission,
                        controlEventRate, controlEventRateDisplay, resource, reference
                    }
                })
        }),
        fetchBaselineRisk: create.asyncThunk(
            async (body, { getState }) => {
                const state = getState();
                const apiToken = selectCurrentUserApiToken(state);
                const resourceEntities = await loadBaselineRiskCollection(body.findingsSection,
                    apiToken, body.combinedDictionary, body.resourceCollection);
                return resourceEntities
            },
            {
                /*
                 options: {
                     condition(arg, thunkApi) {
                         const { baselineRisk } = thunkApi.getState()
                         if (baselineRisk.status !== 'idle') {
                             return false
                         }
                     }
                 },
                 */
                pending: state => {
                    state.status = "loading"
                },
                fulfilled: (state, action) => {
                    state.status = "succeeded"
                    baselineRiskAdapter.setAll(state, action.payload)
                },
                rejected: (state, action) => {
                    state.status = "failed"
                    state.error = action.error.message ?? "Unknown Error"
                },
            },
        ),
    }),
    selectors: {
        selectBaselineRiskStatus: baselineRisk => baselineRisk.status,
        selectBaselineRiskError: baselineRisk => baselineRisk.error
    },
})

export const { baselineRiskAdded, baselineRiskUpdated, fetchBaselineRisk } = baselineRiskSlice.actions

export const { selectBaselineRiskStatus, selectBaselineRiskError } = baselineRiskSlice.selectors

export const {
    selectAll: selectAllBaselineRisks,
    selectById: selectBaselineRiskById,
} = baselineRiskAdapter.getSelectors(state => state.baselineRisk)

export const addBaselineRiskListeners = () => {

    startAppListening({
        matcher: isAnyOf(baselineRiskAdded, baselineRiskUpdated),
        effect: (action, listenerApi) => {
            try {
                const resourceChange = action.payload;
                listenerApi.dispatch(changedResourceAdded({ singleUpdate: resourceChange.id }));
            } catch (err) {
                console.log("FAILURE of baselineRisk listener: ", err)
            }
        }
    })
}

export default baselineRiskSlice